/**
 * Panel de detalles del nodo seleccionado
 * @module components/panels/DetailPanel
 */

import { useState } from 'react'
import { useNodeStore, useUIStore, useGeoStore, useMapHistoryStore } from '@/stores'
import { NODE_ICONS } from '@/constants'
import { X, Edit2, Trash2, Copy, MapPin, Calculator, Link2, Layers, Crosshair, Loader2 } from 'lucide-react'
import { isNAP, isCierre, isPOP, isCliente, isPoste, AnyNode, PuertoNAP } from '@/types/nodes'
import { ConexionInterna } from '@/types/connections'
import { NodeFormModal, OpticalBudgetCalculator, SangradoEditor, PuertosNAPConfig } from '@/components/forms'
import { SpliceDiagram, InternalConnectionsDiagram, EmpalmesDiagram } from '@/components/diagrams'
import { ConnectionsPanel } from './ConnectionsPanel'
import { useFiberStore } from '@/stores'
import { GitBranch, Settings2 } from 'lucide-react'
import { useGeolocation } from '@/hooks/useGeolocation'
import { moverNodoConCables } from '@/utils/moverNodoConCables'
import { napSplittersToSplitterCompleto } from '@/utils/napSplitterDiagramAdapter'

export function DetailPanel() {
  const detailPanelOpen = useUIStore((state) => state.detailPanelOpen)
  const setDetailPanelOpen = useUIStore((state) => state.setDetailPanelOpen)
  const addNotification = useUIStore((state) => state.addNotification)
  const selectedNodeId = useNodeStore((state) => state.selectedNodeId)
  const getNodeById = useNodeStore((state) => state.getNodeById)
  const selectNode = useNodeStore((state) => state.selectNode)
  const deleteNode = useNodeStore((state) => state.deleteNode)
  const updateNode = useNodeStore((state) => state.updateNode)
  const setGeoFix = useGeoStore((s) => s.setLastFix)
  const setFollowOnNext = useGeoStore((s) => s.setFollowOnNext)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [showSangradoEditor, setShowSangradoEditor] = useState(false)
  const [showPuertosConfig, setShowPuertosConfig] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'connections' | 'diagram' | 'budget'>('info')

  const cables = useFiberStore((state) => state.cables)
  const updateCable = useFiberStore((state) => state.updateCable)

  const { getCurrentPosition, loading: gpsLoading } = useGeolocation()

  const node = selectedNodeId ? getNodeById(selectedNodeId) : null

  if (!detailPanelOpen || !node) {
    return null
  }

  const handleClose = () => {
    setDetailPanelOpen(false)
    selectNode(null)
  }

  const handleEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleDelete = async () => {
    const nodeName = (node as any).nombre || node.id
    if (confirm(`¿Estás seguro de eliminar el nodo "${nodeName}"?\n\nEsta acción no se puede deshacer.`)) {
      await deleteNode(node.id)
      handleClose()
    }
  }

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true)
  }

  const handleModalClose = () => {
    setIsEditModalOpen(false)
    setIsDuplicateModalOpen(false)
  }

  const handleMoveToGPS = async () => {
    if (!node) return
    const nombre = (node as any).nombre || node.id
    if (!confirm(`¿Mover "${nombre}" a tu ubicación GPS actual?\n\nLos cables conectados se actualizarán automáticamente.`)) {
      return
    }
    try {
      const fix = await getCurrentPosition({ highAccuracy: true, timeoutMs: 20000 })
      if (fix.accuracy > 50) {
        if (!confirm(`La precisión GPS es baja (±${Math.round(fix.accuracy)} m). ¿Usarla de todos modos?`)) {
          return
        }
      }
      setGeoFix(fix)
      setFollowOnNext(true)

      const result = await moverNodoConCables(
        node,
        { lat: fix.lat, lng: fix.lng },
        {
          updateNode: (n) => updateNode(n as any),
          updateCable: (c) => updateCable(c as any),
          cables: cables as any,
        }
      )
      useMapHistoryStore.getState().pushNodeMove({
        nodeId: result.nodeId,
        before: result.before,
        after: result.after,
        cables: result.cables,
      })
      addNotification('success', `Nodo reubicado a tu GPS (±${Math.round(fix.accuracy)} m)`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al mover el nodo al GPS'
      addNotification('error', msg)
    }
  }

  return (
    <>
      <aside className="w-96 bg-white dark:bg-dark-800 border-l border-gray-200 dark:border-dark-700 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{NODE_ICONS[node.tipo]}</span>
              <div>
                <h2 className="text-lg font-semibold">{(node as any).nombre || (node as any).refCode || node.id}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{node.tipo}</p>
                {(node as any).refCode && (node as any).nombre && (
                  <p className="text-xs font-mono text-primary-600 dark:text-primary-400">{(node as any).refCode}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleEdit}
              className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={handleDuplicate}
              className="px-3 py-2 bg-gray-200 dark:bg-dark-700 text-sm rounded hover:bg-gray-300 dark:hover:bg-dark-600"
              title="Duplicar nodo"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              title="Eliminar nodo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs para nodos que soportan conexiones */}
        {(node.tipo === 'CIERRE' || node.tipo === 'NAP' || node.tipo === 'POP') && (
          <div className="flex border-b border-gray-200 dark:border-dark-700">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1 ${
                activeTab === 'info'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MapPin className="w-3 h-3" />
              Info
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1 ${
                activeTab === 'connections'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Link2 className="w-3 h-3" />
              Conexiones
            </button>
            <button
              onClick={() => setActiveTab('diagram')}
              className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1 ${
                activeTab === 'diagram'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Layers className="w-3 h-3" />
              Diagrama
            </button>
            <button
              onClick={() => setActiveTab('budget')}
              className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1 ${
                activeTab === 'budget'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calculator className="w-3 h-3" />
              Óptico
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Tab: Info */}
          {activeTab === 'info' && (
            <>
              {/* Información básica */}
              <Section title="Información Básica">
                {(node as any).refCode && <InfoRow label="Código" value={(node as any).refCode} />}
                <InfoRow label="ID técnico (UUID)" value={node.id} />
                <InfoRow label="Tipo" value={node.tipo} />
                {(node as any).nombre && <InfoRow label="Nombre" value={(node as any).nombre} />}
                {(node as any).descripcion && (
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Descripción:</span>
                    <p className="mt-1 text-gray-800 dark:text-gray-200">{(node as any).descripcion}</p>
                  </div>
                )}
              </Section>

              {/* Ubicación */}
              <Section title="Ubicación">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{node.lat.toFixed(6)}, {node.lng.toFixed(6)}</span>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${node.lat},${node.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:underline"
                >
                  Ver en Google Maps →
                </a>
                <button
                  type="button"
                  onClick={handleMoveToGPS}
                  disabled={gpsLoading}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 shadow-sm touch-manipulation"
                  title="Mover este nodo a tu ubicación GPS actual"
                >
                  {gpsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Obteniendo GPS…
                    </>
                  ) : (
                    <>
                      <Crosshair className="w-4 h-4" />
                      Poner aquí (mi GPS)
                    </>
                  )}
                </button>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Útil en visita de campo: reubica el nodo exactamente donde estás ahora.
                </p>
              </Section>

              {/* Fechas */}
              <Section title="Fechas">
                <InfoRow
                  label="Creado"
                  value={new Date(node.createdAt).toLocaleString('es-ES', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                />
                {node.updatedAt && (
                  <InfoRow
                    label="Actualizado"
                    value={new Date(node.updatedAt).toLocaleString('es-ES', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  />
                )}
              </Section>

              {/* Información específica por tipo */}
              {isNAP(node) && <NAPDetails node={node} />}
              {isCierre(node) && <CierreDetails node={node} />}
              {isPOP(node) && <POPDetails node={node} />}
              {isCliente(node) && <ClienteDetails node={node} />}
              {isPoste(node) && <PosteDetails node={node} />}
            </>
          )}

          {/* Tab: Conexiones */}
          {activeTab === 'connections' && (node.tipo === 'CIERRE' || node.tipo === 'NAP') && (
            <>
              <ConnectionsPanel
                nodeId={node.id}
                nodeType={node.tipo}
                nodeName={(node as any).nombre}
              />
            </>
          )}

          {/* Tab: Diagrama */}
          {activeTab === 'diagram' && (node.tipo === 'CIERRE' || node.tipo === 'NAP') && (
            <div className="space-y-4">
              {/* Acciones rápidas */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSangradoEditor(true)}
                  className="flex-1 px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <GitBranch className="w-4 h-4" />
                  Crear Sangrado
                </button>
                {node.tipo === 'NAP' && (
                  <button
                    onClick={() => setShowPuertosConfig(true)}
                    className="flex-1 px-3 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 flex items-center justify-center gap-2"
                  >
                    <Settings2 className="w-4 h-4" />
                    Config. Puertos
                  </button>
                )}
              </div>
              
              {/* Diagrama de Conexiones Internas */}
              <InternalConnectionsDiagram
                nodoId={node.id}
                tipoNodo={node.tipo as 'CIERRE' | 'NAP'}
                nombreNodo={(node as any).nombre || node.id}
                conexiones={((node as any).conexionesInternas || []) as ConexionInterna[]}
                splitters={napSplittersToSplitterCompleto((node as any).config?.splitters, node.id)}
                puertos={node.tipo === 'NAP' ? ((node as any).puertos || []) as PuertoNAP[] : undefined}
                onSelectConexion={(conexion) => console.log('Conexión seleccionada:', conexion)}
                onSelectSplitter={(splitter) => console.log('Splitter seleccionado:', splitter)}
                onSelectPuerto={(puerto) => console.log('Puerto seleccionado:', puerto)}
              />

              {/* Empalmes hilo a hilo (tabla + exportar PDF) */}
              <div className="border-t border-gray-200 dark:border-dark-700 pt-4">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Empalmes y derivaciones
                </h4>
                <EmpalmesDiagram nodeId={node.id} />
              </div>
              
              {/* Diagrama de Bandejas (vista esquemática) */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Diagrama de Bandejas</h4>
                <SpliceDiagram
                  puntoEmpalmeNombre={(node as any).nombre || node.id}
                  puntoEmpalmeTipo={node.tipo as 'CIERRE' | 'NAP'}
                  conexiones={[]}
                  bandejasCount={(node as any).bandejas?.length || 3}
                />
              </div>
            </div>
          )}

          {/* Tab: Presupuesto Óptico */}
          {activeTab === 'budget' && (
            <OpticalBudgetCalculator
              distanciaInicial={2}
              empalmesInicial={4}
              conectoresInicial={2}
              splitterInicial="1:8"
            />
          )}
        </div>
      </aside>

      {/* Modal de edición */}
      <NodeFormModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        editNode={node}
      />

      {/* Modal de duplicación */}
      <NodeFormModal
        isOpen={isDuplicateModalOpen}
        onClose={handleModalClose}
        duplicateNode={node}
      />
      
      {/* Modal de Sangrado */}
      {showSangradoEditor && (node.tipo === 'CIERRE' || node.tipo === 'NAP') && (
        <SangradoEditor
          nodoId={node.id}
          tipoNodo={node.tipo}
          cablesDisponibles={cables.filter(c => 
            c.origen === node.id || c.destino === node.id ||
            (c as any).origenId === node.id || (c as any).destinoId === node.id
          )}
          puertosNAP={node.tipo === 'NAP' ? ((node as any).puertos || []) : undefined}
          onSave={(resultado) => {
            console.log('Sangrado creado:', resultado)
            setShowSangradoEditor(false)
          }}
          onCancel={() => setShowSangradoEditor(false)}
        />
      )}
      
      {/* Modal de Configuración de Puertos NAP */}
      {showPuertosConfig && node.tipo === 'NAP' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <PuertosNAPConfig
              napId={node.id}
              nombreNAP={(node as any).nombre || node.id}
              puertos={(node as any).puertos || []}
              splitters={(node as any).splittersCompletos || (node as any).caja?.splitters || []}
              onUpdatePuertos={(puertos) => {
                console.log('Puertos actualizados:', puertos)
              }}
              onClose={() => setShowPuertosConfig(false)}
            />
          </div>
        </div>
      )}
    </>
  )
}

// Componentes auxiliares
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600 dark:text-gray-400">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

// Detalles específicos por tipo de nodo
function NAPDetails({ node }: { node: AnyNode }) {
  const napNode = node as any
  if (!napNode.caja) return null

  return (
    <>
      <Section title="Caja NAP">
        <InfoRow label="Tipo" value={napNode.caja?.tipo || 'N/A'} />
        <InfoRow label="Modelo" value={napNode.caja?.modelo || 'N/A'} />
        <InfoRow label="Capacidad" value={`${napNode.caja?.capacidadMaxima || 0} puertos`} />
      </Section>

      {napNode.puertos && napNode.puertos.length > 0 && (
        <Section title="Puertos">
          <div className="space-y-1">
            {napNode.puertos.slice(0, 5).map((puerto: any) => (
              <div
                key={puerto.puerto}
                className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-dark-700 rounded"
              >
                <span>Puerto {puerto.puerto}</span>
                <span
                  className={`px-2 py-0.5 rounded ${
                    puerto.estado === 'libre'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                  }`}
                >
                  {puerto.estado}
                </span>
              </div>
            ))}
            {napNode.puertos.length > 5 && (
              <p className="text-xs text-gray-500 text-center">
                +{napNode.puertos.length - 5} puertos más
              </p>
            )}
          </div>
        </Section>
      )}
    </>
  )
}

function CierreDetails({ node }: { node: AnyNode }) {
  const cierreNode = node as any
  if (!cierreNode.tipoCaja) return null

  return (
    <Section title="Cierre">
      <InfoRow label="Tipo de Caja" value={cierreNode.tipoCaja || 'N/A'} />
      <InfoRow label="Modelo" value={cierreNode.modelo || 'N/A'} />
      <InfoRow label="Capacidad" value={`${cierreNode.capacidad || 0} fibras`} />
      <InfoRow label="Bandejas" value={cierreNode.bandejas?.length || 0} />
    </Section>
  )
}

function POPDetails({ node }: { node: AnyNode }) {
  const popNode = node as any
  
  return (
    <>
      <Section title="POP/OLT">
        <InfoRow label="Nombre" value={popNode.nombre || 'N/A'} />
        <InfoRow label="Dirección" value={popNode.direccion || 'N/A'} />
      </Section>

      {popNode.equipos && popNode.equipos.length > 0 && (
        <Section title="Equipos">
          <div className="space-y-1">
            {popNode.equipos.map((equipo: any) => (
              <div
                key={equipo.id}
                className="text-xs p-2 bg-gray-50 dark:bg-dark-700 rounded"
              >
                <p className="font-medium">{equipo.tipo}</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {equipo.marca} {equipo.modelo}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  )
}

function ClienteDetails({ node }: { node: AnyNode }) {
  const clienteNode = node as any
  if (!clienteNode.servicio) return null

  return (
    <>
      <Section title="Cliente">
        <InfoRow label="Nombre" value={clienteNode.nombre || 'Sin nombre'} />
        <InfoRow label="Dirección" value={clienteNode.direccion || 'N/A'} />
        {clienteNode.telefono && <InfoRow label="Teléfono" value={clienteNode.telefono} />}
      </Section>

      <Section title="Servicio">
        <InfoRow label="Plan" value={clienteNode.servicio?.plan || 'N/A'} />
        <InfoRow label="Estado" value={clienteNode.servicio?.estado || 'N/A'} />
        <InfoRow label="NAP Asignada" value={clienteNode.servicio?.napAsignada || 'N/A'} />
        <InfoRow label="Puerto NAP" value={clienteNode.servicio?.puertoNAP || 'N/A'} />
      </Section>
    </>
  )
}

function PosteDetails({ node }: { node: AnyNode }) {
  const posteNode = node as any
  if (!posteNode.tipoPoste) return null

  return (
    <Section title="Poste">
      <InfoRow label="Tipo" value={posteNode.tipoPoste || 'N/A'} />
      <InfoRow label="Altura" value={`${posteNode.altura || 0}m`} />
      <InfoRow label="Propietario" value={posteNode.propietario || 'N/A'} />
      <InfoRow label="Estado" value={posteNode.estadoFisico || 'N/A'} />
    </Section>
  )
}
